import memoize from 'lodash.memoize';
import { cx } from 'emotion';

/**
 * @param {Array<function(...any): void>} handlers the event handler functions
 * @returns {function(...any): void} An event handler to add to an element
 */
function createAggregateEventHandler(handlers) {
  /**
   * This function needed to be moved out of `combineEventHandlers`
   * since babel doesn't handle nested functions using rest operators
   * properly.
   */
  return function aggregateEventHandler() {
    for (var _len = arguments.length, args = Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    handlers.forEach(function (handler) {
      if (handler) handler.apply(undefined, args);
    });
  };
}

/**
 * Combines multiple event handlers into one
 * @param {...function(...any): void} handlers the event handler functions
 * @returns {function(...any): void} An event handler to add to an element
 */
function combineEventHandlers() {
  for (var _len2 = arguments.length, handlers = Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    handlers[_key2] = arguments[_key2];
  }

  return createAggregateEventHandler(handlers);
}

function memoizeCombineEventHandlers() {
  return memoize(combineEventHandlers);
}

function _toConsumableArray(arr) { if (Array.isArray(arr)) { for (var i = 0, arr2 = Array(arr.length); i < arr.length; i++) { arr2[i] = arr[i]; } return arr2; } else { return Array.from(arr); } }

/**
 * @typedef {Object} ButtonEventHandlers
 * @property {function(MouseEvent, ...any): void} handleClick
 * @property {function(KeyboardEvent, ...any): void} handleKeyDown
 */

/**
 * @typedef {Object} Options
 * @property {boolean} [preventDefault]
 */

var KEYBOARD_INTERACTIONS = [" ", "Enter"];

/**
 * Create event handlers for native button behavior for non-button elements
 * @see https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/button_role
 *
 * @param {function(MouseEvent|KeyboardEvent, ...any): void} [handler] the event handler function
 * @param {Options} [options]
 * @returns {ButtonEventHandlers}
 */
function createButtonEventHandlers(handler) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

  if (!handler) return {};

  var _options$preventDefau = options.preventDefault,
      preventDefault = _options$preventDefau === undefined ? true : _options$preventDefau;


  return {
    handleClick: handler,
    handleKeyDown: function handleKeyDown(event) {
      var key = event.key;


      if (!KEYBOARD_INTERACTIONS.includes(key)) return;
      // Prevent space key default scrolling behavior
      if (preventDefault) event.preventDefault();

      for (var _len = arguments.length, args = Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
        args[_key - 1] = arguments[_key];
      }

      handler.apply(undefined, [event].concat(_toConsumableArray(args)));
    }
  };
}

function memoizeCreateButtonEventHandlers() {
  return memoize(createButtonEventHandlers);
}

/**
 * Generates a unique ID
 * @param {string} key
 * @returns {string} A generated id
 */
function generateId(key) {
  return key + "-" + Math.floor(Math.random() * 100000, 5);
}

/**
 * Returns a css className that combines what the user passes down and a string.
 * Ideally the string would map to the element's js style object's name.
 * @param {string} className
 * @param {string} appendedString
 * @returns {string} A string that looks like className__appendedString
 */

function createCustomClassNames(className, appendedString) {
  return className && className.split(" ").reduce(function (acc, cur) {
    return cx(acc, cur.trim() + "__" + appendedString);
  }, "");
}

/* eslint-disable import/prefer-default-export */

export { combineEventHandlers, memoizeCombineEventHandlers, createButtonEventHandlers, memoizeCreateButtonEventHandlers, generateId, createCustomClassNames };
